import { useState, useCallback, useEffect } from 'react';
import type { TabStructure } from '../components';
import useUrlParameters from './useUrlParameters';

/**
 * Props pour le hook useFlexyTabs
 */
type UseFlexyTabsOptions = {
    /** Liste des onglets disponibles */
    tabs: Array<TabStructure>;
    /** Nom du paramètre d'URL pour stocker l'onglet actif */
    routerParameterName: string;
    /** Utilise un état local au lieu de l'URL */
    useLocalState: boolean;
    /** Onglet par défaut à utiliser en mode local */
    defaultTab?: string;
    /** Callback appelé lors du changement d'onglet */
    onTabChange?: (tabName: string) => void;
    /** Nom de l'onglet actif (pour contrôler le composant de l'extérieur) */
    activeTabName?: string;
};

/**
 * Résultat retourné par le hook useFlexyTabs
 */
type UseFlexyTabsResult = {
    /** Index de l'onglet actif */
    activeTabIndex: number;
    /** Gestionnaire pour changer d'onglet */
    handleTabChange: (event: React.SyntheticEvent | null, newValue: number) => void;
};

/**
 * Hook personnalisé pour gérer la logique des onglets dans FlexyTabs
 *
 * Supporte deux modes de fonctionnement:
 * - Mode URL: l'onglet actif est stocké dans l'URL
 * - Mode local: l'onglet actif est géré par un état local
 */
export function useFlexyTabs({
    tabs,
    routerParameterName,
    useLocalState,
    defaultTab,
    onTabChange,
    activeTabName,
}: UseFlexyTabsOptions): UseFlexyTabsResult {
    const { urlParameters, addUrlParameters } = useUrlParameters();
    const [activeTabIndex, setActiveTabIndex] = useState<number>(0);

    /**
     * Récupère le nom de l'onglet à partir de son index
     */
    const getTabNameFromIndex = useCallback((index: number) => tabs[index]?.tabName, [tabs]);

    /**
     * Récupère l'index de l'onglet à partir de son nom
     */
    const getTabIndexFromName = useCallback(
        (name: string) => tabs.findIndex((tab) => tab.tabName === name && tab.disabled !== true),
        [tabs],
    );

    /**
     * Récupère le paramètre d'onglet depuis l'URL
     */
    const getUrlTabParameter = useCallback(() => {
        if (useLocalState) return undefined;

        const allowedTabNames = tabs.map((tab) => tab.tabName);
        return allowedTabNames.includes(urlParameters[routerParameterName])
            ? urlParameters[routerParameterName]
            : undefined;
    }, [routerParameterName, tabs, urlParameters, useLocalState]);

    /**
     * Détermine l'index de l'onglet à afficher en fonction du contexte
     */
    const determineActiveTabIndex = useCallback(() => {
        // 1. Si on est en mode local et qu'un onglet actif est spécifié
        if (useLocalState && activeTabName) {
            const indexFromName = getTabIndexFromName(activeTabName);
            if (indexFromName !== -1) return indexFromName;
        }

        // 2. Si on est en mode local et qu'un onglet par défaut est spécifié
        if (useLocalState && defaultTab) {
            const indexFromDefault = getTabIndexFromName(defaultTab);
            if (indexFromDefault !== -1) return indexFromDefault;
        }

        // 3. Si on est en mode URL, on cherche dans les paramètres d'URL
        if (!useLocalState) {
            const urlTabParameter = getUrlTabParameter();
            if (urlTabParameter) {
                const index = getTabIndexFromName(urlTabParameter);
                if (index !== -1) return index;
            }
        }

        // 4. Par défaut, on prend le premier onglet non désactivé
        const firstEnabledTab = tabs.findIndex((tab) => tab.disabled !== true);
        if (firstEnabledTab !== -1) return firstEnabledTab;

        // 5. Si tous les onglets sont désactivés, on renvoie 0
        return 0;
    }, [useLocalState, activeTabName, defaultTab, getTabIndexFromName, getUrlTabParameter, tabs]);

    /**
     * Gère le changement d'onglet
     */
    const handleTabChange = useCallback(
        (event: React.SyntheticEvent | null, newValue: number) => {
            const newTabName = getTabNameFromIndex(newValue);

            if (!newTabName) return;

            // Mise à jour de l'index de l'onglet actif
            setActiveTabIndex(newValue);

            if (useLocalState) {
                // En mode local, on appelle le callback
                if (onTabChange) {
                    onTabChange(newTabName);
                }
            } else {
                // En mode URL, on met à jour l'URL
                const toAdd: Record<string, string | string[]> = {};
                toAdd[routerParameterName] = newTabName;
                addUrlParameters(toAdd);
            }
        },
        [getTabNameFromIndex, useLocalState, onTabChange, routerParameterName, addUrlParameters],
    );

    // Initialisation lors du premier rendu et réaction aux changements externes
    useEffect(() => {
        const tabIndex = determineActiveTabIndex();
        if (tabIndex !== activeTabIndex) {
            setActiveTabIndex(tabIndex);
        }
    }, [determineActiveTabIndex, activeTabIndex]);

    // Réaction aux changements de activeTabName en mode local
    useEffect(() => {
        if (useLocalState && activeTabName) {
            const indexFromName = getTabIndexFromName(activeTabName);
            if (indexFromName !== -1 && indexFromName !== activeTabIndex) {
                setActiveTabIndex(indexFromName);
            }
        }
    }, [activeTabName, useLocalState, getTabIndexFromName, activeTabIndex]);

    return {
        activeTabIndex,
        handleTabChange,
    };
}
